<script setup lang="ts">
/**
 * Calls out an offer whose copy is still PENDING, so an admin browsing the offers does not mistake it for something
 * patrons can see. Only admins are ever sent a PENDING offer, so this only ever renders for them. Same two shapes as
 * App\components\offers\OfferRedeemedStatus.vue: a <peppermill-badge> for the offer cards in the grid, and a
 * full-width banner for the single-offer page.
 *
 * Renders nothing at all for an offer that is live, so callers can drop it in unconditionally.
 *
 * @changelog
 *
 * 2026-09-23 (0.1.0) (jhoward)
 *   - Add App\components\offers\OfferPendingStatus.vue, flagging PENDING offers as not live for patrons yet.
 */

import PeppermillBadge from '@framework/components/PeppermillBadge.vue';
import type { PropType } from 'vue';
import { computed } from 'vue';
import type { OpcOffer } from '@/defs/api/OpcOffer';
import isOfferPending from '@/helpers/isOfferPending';

const props = defineProps({
  offer: {
    type: Object as PropType<OpcOffer>,
    required: true,
  },
  //Full-width banner for the detail page. The compact badge is used on cards.
  isBanner: {
    type: Boolean,
    default: false,
  },
});

const isPending = computed(() => isOfferPending(props.offer));
</script>

<template>
  <template v-if="isPending">
    <div v-if="isBanner" class="offer_pending--banner">
      <peppermill-badge label="Pending" icon="visibility_off" variant="warning" mod="solid"></peppermill-badge>
      <p class="offer_pending--detail">This offer is not live yet. Patrons cannot see it, only admins can.</p>
    </div>

    <peppermill-badge
      v-else
      label="Pending - not live for patrons"
      icon="visibility_off"
      variant="warning"
      mod="solid"
      tooltip="Only admins can see this offer until it goes live."
    ></peppermill-badge>
  </template>
</template>

<style scoped>
/*Sits above the offer title, beside the redeemed banner if there is one, so it is the first thing read on the page.*/
.offer_pending--banner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5em 0.75em;
  margin-bottom: 1em;
  padding: 0.75em 1em;
  border-left: 4px solid rgb(var(--status-warning));
  border-radius: 0.25em;
  background-color: rgba(var(--status-warning), 0.12);
}

/*Full-contrast body text rather than the warning colour, for the same reason as the redeemed banner: the badge
  beside it already carries the status colour.*/
.offer_pending--banner .offer_pending--detail {
  margin: 0;
  min-width: 0;
}
</style>
